import { useEffect, useState } from 'react';

import { CLAUDE_CODE, MCP_CLIENTS, TOKEN_PLACEHOLDER } from '@/components/mcp/clients';
import { CopyButton } from '@/components/ui/copy-button';
import { Segmented } from '@/components/ui/segmented';
import { TabPanel, Tabs } from '@/components/ui/tabs';

type Method = 'oauth' | 'token';

/**
 * クライアント別の接続手順。
 *
 * 手順を散文で書くと読まれないので、貼るブロックを 1 つと補足だけにする。
 *
 * **接続の方法は 2 つ。** 既定はブラウザで許可する方 (OAuth) —— 平文の
 * トークンが設定ファイルにも手元にも残らないため。対応していない
 * クライアントのために、トークンを貼る方も残す。トークンは発行直後にだけ
 * 実物を差し込む (平文は発行時の応答にしか存在しないので、後から埋め直せない)。
 */
export function ClientSetup({ endpoint, token }: { endpoint: string; token: string | null }) {
  const [active, setActive] = useState(CLAUDE_CODE.id);
  const [method, setMethod] = useState<Method>('oauth');
  /*
   * トークンを発行したら、その平文を差し込める側に寄せる。**初期値では
   * できない** —— 発行はこの部品が描かれた後に起きるので、初期値で分岐すると
   * 発行した平文が見えないまま「ブラウザで許可」の手順が出続ける。
   */
  useEffect(() => {
    if (token !== null) setMethod('token');
  }, [token]);
  const client = MCP_CLIENTS.find((item) => item.id === active) ?? CLAUDE_CODE;
  const snippet =
    method === 'oauth'
      ? client.oauthSnippet(endpoint)
      : client.snippet(endpoint, token ?? TOKEN_PLACEHOLDER);

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm font-bold">{'クライアントの設定'}</span>
        <Tabs
          value={active}
          onChange={setActive}
          options={MCP_CLIENTS.map((item) => ({ value: item.id, label: item.label }))}
          label="AI クライアント"
        />
      </div>

      <TabPanel value={client.id}>
        <div className="flex flex-col gap-3">
          <Segmented
            value={method}
            onChange={setMethod}
            options={[
              { value: 'oauth', label: 'ブラウザで許可' },
              { value: 'token', label: 'トークンを貼る' },
            ]}
            label="接続の方法"
          />

          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] tracking-wider text-ink-faint">
              {client.target ?? 'TERMINAL'}
            </span>
            <CopyButton value={snippet} />
          </div>

          <pre className="overflow-x-auto bg-surface px-4 py-3 font-mono text-xs text-ink-secondary">
            <code>{snippet}</code>
          </pre>

          {method === 'oauth' ? (
            <p className="text-[13px] leading-relaxed text-ink-muted">
              {
                '初回の接続でブラウザが開き、この画面と同じログインで許可を求められます。クライアントが対応していないときは「トークンを貼る」に切り替えてください。'
              }
            </p>
          ) : null}

          {method === 'token' && token === null ? (
            <p className="text-[13px] text-ink-muted">
              <span className="font-mono">{TOKEN_PLACEHOLDER}</span>
              {' は上で発行したトークンに置き換えてください。'}
            </p>
          ) : null}

          <ul className="flex flex-col gap-1">
            {client.notes.map((note) => (
              <li key={note} className="text-[13px] leading-relaxed text-ink-muted">
                {note}
              </li>
            ))}
          </ul>
        </div>
      </TabPanel>
    </div>
  );
}
